import type { LcDirectory, LcNapSource, LcStats, LcTierDef } from './types';

function mockDir(id: string, rest: Omit<LcDirectory, 'id' | 'directoryKey'>): LcDirectory {
  return { id, directoryKey: id, ...rest };
}

export const LC_NAP: LcNapSource = {
  name: 'Smile Central Dental',
  address: '123 Fifth Avenue, Suite 400, New York, NY 10003',
  phone: '(212) 555-0100',
  website: 'https://smilecentraldental.com',
  hours: 'Mon–Fri 8am–6pm · Sat 9am–3pm · Sun closed',
  lastUpdated: 'from PMS · 2 weeks ago',
};

const LC_TIER1: LcDirectory[] = [
  mockDir('google', { name: 'Google Business Profile', logo: 'G', status: 'consistent', lastChecked: '2 hours ago' }),
  mockDir('yelp', {
    name: 'Yelp',
    logo: 'Y',
    status: 'inconsistent',
    issue: 'Phone number: (212) 555-0101 · Correcting',
    lastChecked: '1 hour ago',
  }),
  mockDir('facebook', { name: 'Facebook', logo: 'f', status: 'consistent', lastChecked: '3 hours ago' }),
  mockDir('apple', { name: 'Apple Maps', logo: 'A', status: 'consistent', lastChecked: '1 day ago' }),
  mockDir('bing', {
    name: 'Bing Places',
    logo: 'B',
    status: 'inconsistent',
    issue: 'Address: Missing suite number · Correcting',
    lastChecked: '6 hours ago',
  }),
  mockDir('insta', { name: 'Instagram', logo: 'IG', status: 'consistent', lastChecked: '2 days ago' }),
  mockDir('nextdoor', { name: 'Nextdoor', logo: 'N', status: 'missing', lastChecked: '—' }),
  mockDir('duckduck', { name: 'DuckDuckGo Maps', logo: 'D', status: 'consistent', lastChecked: '1 day ago' }),
];

const LC_TIER2: LcDirectory[] = [
  mockDir('hg', { name: 'Healthgrades', logo: 'HG', status: 'consistent', lastChecked: '12 hours ago' }),
  mockDir('zocdoc', {
    name: 'Zocdoc',
    logo: 'Z',
    status: 'inconsistent',
    issue: 'Hours: Showing Sat closed · Correcting',
    lastChecked: '8 hours ago',
  }),
  mockDir('vitals', {
    name: 'Vitals.com',
    logo: 'V',
    status: 'duplicate',
    issue: '2 listings found — one with old address',
    lastChecked: '1 day ago',
  }),
  mockDir('webmd', { name: 'WebMD Directory', logo: 'W', status: 'consistent', lastChecked: '2 days ago' }),
  mockDir('ratemds', { name: 'RateMDs', logo: 'R', status: 'consistent', lastChecked: '1 day ago' }),
  mockDir('drnearme', { name: 'DoctorNearMe', logo: 'DN', status: 'missing', lastChecked: '—' }),
  mockDir('1800dentist', {
    name: '1-800-DENTIST',
    logo: '1D',
    status: 'unclaimed',
    issue: "Listing exists but hasn't been claimed",
    lastChecked: '3 days ago',
  }),
];

const LC_TIER3: LcDirectory[] = [
  mockDir('yp', { name: 'YellowPages', logo: 'YP', status: 'consistent', lastChecked: '3 days ago' }),
  mockDir('foursquare', { name: 'Foursquare', logo: '4S', status: 'consistent', lastChecked: '2 days ago' }),
  mockDir('mapquest', {
    name: 'MapQuest',
    logo: 'MQ',
    status: 'inconsistent',
    issue: 'Phone: Old number · Correcting',
    lastChecked: '4 days ago',
  }),
  mockDir('citysearch', { name: 'Citysearch', logo: 'CS', status: 'consistent', lastChecked: '5 days ago' }),
  mockDir('manta', { name: 'Manta', logo: 'M', status: 'missing', lastChecked: '—' }),
  mockDir('merchantcircle', { name: 'MerchantCircle', logo: 'MC', status: 'consistent', lastChecked: '6 days ago' }),
];

export const LC_TIERS: LcTierDef[] = [
  {
    tier: 1,
    title: 'Tier 1 — Critical directories',
    subtitle: 'The highest-impact citations. Matters most for Map Pack ranking.',
    badge: 'High impact',
    tone: 'ai',
    directories: LC_TIER1,
    defaultOpen: true,
  },
  {
    tier: 2,
    title: 'Tier 2 — Dental / medical directories',
    subtitle: 'Industry-specific listings. Important for patient discovery.',
    tone: 'info',
    directories: LC_TIER2,
  },
  {
    tier: 3,
    title: 'Tier 3 — General business directories',
    subtitle: 'Broad citations that reinforce your online footprint.',
    tone: 'neutral',
    directories: LC_TIER3,
  },
];

const LC_ALL = [...LC_TIER1, ...LC_TIER2, ...LC_TIER3];

function lcCount(status: LcDirectory['status']) {
  return LC_ALL.filter((d) => d.status === status).length;
}

export const LC_STATS: LcStats = {
  total: LC_ALL.length,
  consistent: lcCount('consistent'),
  inconsistent: lcCount('inconsistent'),
  missing: lcCount('missing'),
  duplicate: lcCount('duplicate'),
  unclaimed: lcCount('unclaimed'),
  recentFixes: 12,
};

export const LC_STATUS_LABELS: Record<
  LcDirectory['status'],
  { label: string; tone: import('./types').LcToneKey }
> = {
  consistent: { label: 'Consistent', tone: 'success' },
  inconsistent: { label: 'Correcting', tone: 'gold' },
  missing: { label: 'Not listed', tone: 'info' },
  duplicate: { label: 'Duplicate', tone: 'terra' },
  unclaimed: { label: 'Unclaimed', tone: 'lav' },
};

export const LC_ACTIONS: Partial<
  Record<LcDirectory['status'], { label: string; tone: import('./types').LcToneKey }>
> = {
  missing: { label: 'Create listing', tone: 'info' },
  duplicate: { label: 'Help resolve', tone: 'terra' },
  unclaimed: { label: 'Claim listing', tone: 'lav' },
};
